import React, { useState, useCallback } from 'react'
import { Box, Typography, Table, Sheet, Button, CircularProgress, Alert, Chip } from '@mui/joy'
import { Refresh, CheckCircle, Warning } from '@mui/icons-material'
import { billingApi } from './api'
import { QuotaHealthItem } from './types'

interface Props {
    appId: string
}

export default function QuotaHealthCheck({ appId }: Props) {
    const [items, setItems] = useState<QuotaHealthItem[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [hasLoaded, setHasLoaded] = useState(false)

    const runCheck = useCallback(async () => {
        try {
            setLoading(true)
            setError(null)
            const data = await billingApi.getQuotaHealth(appId)
            setItems(data)
            setHasLoaded(true)
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to run health check')
        } finally {
            setLoading(false)
        }
    }, [appId])

    const outOfSyncCount = items.filter((i) => !i.inSync).length

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography level="title-lg">Quota Health Check</Typography>
                    <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                        Compare quota state across Redis (real-time) and PostgreSQL (~5min batch)
                    </Typography>
                </Box>
                <Button variant="solid" color="primary" onClick={runCheck} loading={loading} startDecorator={<Refresh />}>
                    {hasLoaded ? 'Refresh' : 'Run Check'}
                </Button>
            </Box>

            {error && (
                <Alert color="danger" variant="soft" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {hasLoaded && (
                <>
                    {outOfSyncCount > 0 ? (
                        <Alert color="warning" variant="soft" sx={{ mb: 2 }} startDecorator={<Warning />}>
                            {outOfSyncCount} feature(s) out of sync across data stores
                        </Alert>
                    ) : (
                        <Alert color="success" variant="soft" sx={{ mb: 2 }} startDecorator={<CheckCircle />}>
                            All features in sync
                        </Alert>
                    )}

                    <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                        <Table stickyHeader hoverRow size="sm">
                            <thead>
                                <tr>
                                    <th>Feature</th>
                                    <th style={{ textAlign: 'center' }}>Redis</th>
                                    <th style={{ textAlign: 'center' }}>PostgreSQL</th>
                                    <th style={{ textAlign: 'center' }}>Limit</th>
                                    <th style={{ textAlign: 'center' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} style={{ textAlign: 'center', padding: '1rem' }}>
                                            No features with hard limits found
                                        </td>
                                    </tr>
                                ) : (
                                    items.map((item) => (
                                        <tr
                                            key={item.featureKey}
                                            style={{
                                                backgroundColor: !item.inSync ? 'var(--joy-palette-danger-softBg)' : undefined,
                                            }}
                                        >
                                            <td>
                                                <Typography level="body-sm" fontWeight="md">
                                                    {item.featureKey}
                                                </Typography>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>{item.redis ?? '-'}</td>
                                            <td style={{ textAlign: 'center' }}>{item.postgresql ?? '-'}</td>
                                            <td style={{ textAlign: 'center' }}>{item.limit ?? 'unlimited'}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <Chip size="sm" variant="soft" color={item.inSync ? 'success' : 'danger'}>
                                                    {item.inSync ? 'In Sync' : 'Out of Sync'}
                                                </Chip>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </Sheet>
                </>
            )}

            {!hasLoaded && !loading && (
                <Alert color="neutral" variant="soft">
                    Click "Run Check" to compare quota state across data stores
                </Alert>
            )}
        </Box>
    )
}
